import { useId, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import type {
  AffiliateCodeCreateRequest,
  AffiliateCodeLabelRequest,
  AffiliateCodeView,
} from '@/api/generated';
import { useToast } from '@/components/useToast';
import { visuallyHidden } from '@/components/visuallyHidden';
import { useCanWrite } from '@/auth/authHooks';
import { StatusChip } from '@/components/status/StatusChip';
import { Icon, resolveTokens } from '@/theme';
import { EditCodeLabelDialog } from './EditCodeLabelDialog';
import { NewCodeDialog } from './NewCodeDialog';
import { ActionErrorAlert } from '@/components/ActionErrorAlert';
import { useActionError, type ActionError } from '@/components/useActionError';

export interface CodesPanelProps {
  codes: AffiliateCodeView[];
  onCreate: (body: AffiliateCodeCreateRequest) => Promise<void>;
  onEditLabel: (codeId: number, body: AffiliateCodeLabelRequest) => Promise<void>;
  onSetStatus: (codeId: number, status: 'PAUSED' | 'ACTIVE') => Promise<void>;
}

export function CodesPanel({ codes, onCreate, onSetStatus, onEditLabel }: CodesPanelProps) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const showToast = useToast();
  const describe = useActionError();
  const canWrite = useCanWrite();
  const headingId = useId();
  const [editing, setEditing] = useState<AffiliateCodeView | null>(null);
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<ActionError | null>(null);

  async function handleCreate(body: AffiliateCodeCreateRequest) {
    await onCreate(body);
    showToast(t('affiliates.toast.codeCreated', { code: body.code }));
  }

  async function handleCopy(link: string) {
    const clipboard = navigator.clipboard;
    if (clipboard === undefined || typeof clipboard.writeText !== 'function') {
      showToast(t('affiliates.toast.copyError'), 'error');
      return;
    }
    try {
      await clipboard.writeText(link);
      showToast(t('affiliates.toast.copied'));
    } catch {
      showToast(t('affiliates.toast.copyError'), 'error');
    }
  }

  async function handleStatus(code: AffiliateCodeView) {
    if (code.id === undefined) return;
    setBusyId(code.id);
    setActionError(null);
    try {
      const next = code.status === 'PAUSED' ? 'ACTIVE' : 'PAUSED';
      await onSetStatus(code.id, next);
      showToast(
        t(next === 'PAUSED' ? 'affiliates.toast.codePaused' : 'affiliates.toast.codeResumed', {
          code: code.code ?? '',
        }),
      );
    } catch (err) {
      setActionError(describe(err));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <Paper sx={{ p: 2.5, minWidth: 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h3" component="h3" id={headingId}>
          {t('affiliates.code.title')}
        </Typography>
        {canWrite ? (
          <Button onClick={() => setOpen(true)}>{t('affiliates.code.new')}</Button>
        ) : null}
      </Box>
      <ActionErrorAlert error={actionError} sx={{ mb: 1.5 }} />
      {codes.length === 0 ? (
        <Typography variant="body2" sx={{ color: tokens.text2 }}>
          {t('affiliates.code.empty')}
        </Typography>
      ) : (
        <Table size="small" aria-labelledby={headingId}>
          <TableHead>
            <TableRow>
              <TableCell>{t('affiliates.code.code')}</TableCell>
              <TableCell>{t('affiliates.code.label')}</TableCell>
              <TableCell>{t('affiliates.code.link')}</TableCell>
              <TableCell>{t('affiliates.code.status')}</TableCell>
              <TableCell>
                <Box component="span" sx={visuallyHidden}>
                  {t('common.actions')}
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {codes.map((code) => (
              <TableRow key={code.id ?? code.code}>
                <TableCell component="th" scope="row">
                  {code.code ?? t('affiliates.unavailable')}
                </TableCell>
                <TableCell>{code.label ?? t('affiliates.unavailable')}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {code.link ?? t('affiliates.unavailable')}
                    </Typography>
                    {code.link ? (
                      <Button size="small" onClick={() => void handleCopy(code.link ?? '')}>
                        {t('affiliates.code.copy')}
                      </Button>
                    ) : null}
                  </Box>
                </TableCell>
                <TableCell>
                  <StatusChip domain="code" status={code.status} />
                </TableCell>
                <TableCell>
                  {canWrite && code.editableFields?.includes('label') ? (
                    <Tooltip title={t('affiliates.code.editLabel', { code: code.code })}>
                      <IconButton
                        aria-label={t('affiliates.code.editLabel', { code: code.code })}
                        onClick={() => setEditing(code)}
                      >
                        <Icon name="edit" />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                  {canWrite ? (
                    <Button
                      size="small"
                      disabled={busyId === code.id}
                      onClick={() => void handleStatus(code)}
                    >
                      {code.status === 'PAUSED'
                        ? t('affiliates.code.resume')
                        : t('affiliates.code.pause')}
                    </Button>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {editing ? (
        <EditCodeLabelDialog
          code={editing}
          onClose={() => setEditing(null)}
          onSave={async (id, body) => {
            await onEditLabel(id, body);
            showToast(t('affiliates.toast.labelSaved'));
          }}
        />
      ) : null}
      <NewCodeDialog open={open} onClose={() => setOpen(false)} onCreate={handleCreate} />
    </Paper>
  );
}
